import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarUploadDocumentResponse, type SugarVerificationCategory } from '@app/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export const useUploadDocument = ({
  enrollmentId,
  programYearId
}: {
  enrollmentId: string
  programYearId: string
}) => {
  const queryClient = useQueryClient()
  const { siteId } = useSugarContext()

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async ({ category, file }: { category: SugarVerificationCategory; file: File }) => {
      const formData = new FormData()
      formData.append('enrollmentId', enrollmentId)
      formData.append('programYearId', programYearId)
      formData.append('category', category)
      formData.append('file', file, file.name)

      const { data } = await apiClient.post<SugarUploadDocumentResponse>('/api/documents/upload', formData)

      // App-envelope error path (HTTP 200 with success:false). Throwing here lets callers
      // catch via mutateAsync's promise rejection.
      if (!data.success) {
        throw new Error(data.messages?.[0]?.message ?? 'Upload failed.')
      }

      return data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['uploaded-documents', { enrollmentId, programYearId, siteId }]
      })
  })

  return {
    isUploadingDocument: isPending,
    uploadDocument: mutateAsync
  }
}
